import { CssBaseline, ThemeProvider, createTheme, Box, Container, Stack } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import landingPageTheme from '@/@mantis/theme/landingPageTheme';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import '@/styles/landingPage.css';
import { fixdaiTheme } from '@/themes/fixdaiTheme';

import FixdaiHeroSection from './sections/FixdaiHeroSection';
import FooterSection from './sections/FooterSection';
import NavSection from './sections/NavSection';
import PMSecuritySection from './sections/PMSecuritySection';
import TerraiHeroSection from './sections/TerraiHeroSection';
import VendorHowItWorksSection from './sections/VendorHowItWorksSection';
import VendorSearchSection from './sections/VendorSearchSection';

// Custom wrapper for the landing page - domain aware
const PMLandingPageWrapper: React.FC<{ children: React.ReactNode; isFixdDomain: boolean }> = ({
  children,
  isFixdDomain,
}) => {
  // Use Fixd.ai theme if on fixd.ai domain, otherwise use landing page theme
  const theme = isFixdDomain ? fixdaiTheme : createTheme(landingPageTheme);

  // Add preload link for Nohemi font to improve loading on mobile
  useEffect(() => {
    const fontPreloadLinks = [
      { href: '/assets/fonts/Nohemi-Regular.woff2', type: 'font/woff2' },
      { href: '/assets/fonts/Nohemi-SemiBold.woff', type: 'font/woff' },
    ];

    fontPreloadLinks.forEach(font => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = font.href;
      link.as = 'font';
      link.type = font.type;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    return () => {
      fontPreloadLinks.forEach(font => {
        const links = document.querySelectorAll(`link[href="${font.href}"]`);
        links.forEach(link => link.remove());
      });
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>{children}</div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const PropertyManagementLandingPage: React.FC = () => {
  const domainConfig = useDomainConfig();
  const securityRef = useRef<HTMLDivElement>(null);
  const campaign = 'property_owner';
  const isFixdDomain = domainConfig.type === 'fixdai';

  useEffect(() => {
    localStorage.setItem('campaign', campaign);

    // Update favicon dynamically
    const faviconLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (faviconLink) {
      faviconLink.href = domainConfig.favicon;
    }
  }, [campaign, domainConfig.favicon]);

  // Render hero section based on domain
  const renderHeroSection = () => {
    if (isFixdDomain) {
      return <FixdaiHeroSection />;
    }
    return <TerraiHeroSection />;
  };

  return (
    <PMLandingPageWrapper isFixdDomain={isFixdDomain}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{domainConfig.metaTitle}</title>
        <meta name="description" content={domainConfig.metaDescription} />
        <meta name="keywords" content={domainConfig.metaKeywords.join(', ')} />
        <link rel="canonical" href={`https://${domainConfig.domain}`} />

        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={domainConfig.metaTitle} />
        <meta property="og:description" content={domainConfig.metaDescription} />
        <meta property="og:url" content={`https://${domainConfig.domain}`} />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={domainConfig.metaTitle} />
        <meta name="twitter:description" content={domainConfig.metaDescription} />
      </Helmet>

      <NavSection simplified campaign={campaign} />

      {/* Hero and Search Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          position: 'relative',
          bgcolor: isFixdDomain ? '#FFFFFF' : 'transparent',
          minHeight: '80vh',
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={4} alignItems="center" mt={8}>
            {renderHeroSection()}
            <VendorSearchSection />
          </Stack>
        </Container>
      </Box>

      {!isFixdDomain && (
        <>
          <VendorHowItWorksSection />
          <PMSecuritySection ref={securityRef} />
        </>
      )}
      <FooterSection isFixdDomain={isFixdDomain} />
    </PMLandingPageWrapper>
  );
};

export default PropertyManagementLandingPage;
